import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { ISignupResult } from 'src/app/model/account/ISignupResult';
import { GTMOnboardingEvents, OnboardingSteps } from 'src/app/model/google-tag-manager/api-status';
import { AccountService } from 'src/app/core/services/account.service';
import { OktaAuthStateService } from '@okta/okta-angular';
import { contractnumberQueryParam } from '../../../model/constants/query-param.constants';
import { PreInitSettingsService } from '../../../core/services/settings/pre-init-setting.service';
import { HostListener } from '@angular/core';
import { PGHOktaService } from 'src/app/core/services/pghokta.service';
import { ADPResourceService, CustomFormControl } from 'digital-core';
import { GlobalModalWindowService } from 'src/app/core/services/global-modal-window.service';

enum AccountSetupFlow {
  contractNum,
  emailConfirm,
  passwordSetup,
  avatar,
  success,
  weKnowYou
}

@Component({
  selector: 'app-account-setup',
  templateUrl: './account-setup.component.html',
  styleUrls: ['./account-setup.component.scss']
})
export class AccountSetupComponent implements OnInit {
  // For template access
  AccountSetupFlow = AccountSetupFlow;
  password: string;
  mobileNumber: string;
  avatarUrl: string;
  heading: string;
  maskEmailAddress = '';
  contractZipError: string;
  syntheticToken = '';
  reCaptchaInvisbleKey = '';
  isSignupCompleting: boolean = false;
  currentScreenFlow: AccountSetupFlow = AccountSetupFlow.contractNum;
  isAuthenticated = false;
  userAlreadyExistInUserGroup: boolean = false;
  userAlreadyExist: boolean = false;

  contractNumberControl = new CustomFormControl();
  zipControl = new CustomFormControl();

  public findContractForm = new UntypedFormGroup({
    contractNumber: this.contractNumberControl,
    zipCode: this.zipControl,
  })

  constructor(
    private oktaAuthService: OktaAuthStateService,
    private accountService: AccountService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
    private preInitAppSettings: PreInitSettingsService,
    private pghOktaService: PGHOktaService,
    private globalModalWindowService: GlobalModalWindowService,
    public resourceService: ADPResourceService,
  ) {
    this.oktaAuthService.authState$.subscribe(authState => this.isAuthenticated = authState.isAuthenticated);
    this.route.queryParams.subscribe((params) => {
      if (params['st']) {
        this.syntheticToken = params['st'];
      }
    });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    this.onClose();
  }

  async ngOnInit() {
    this.isAuthenticated = this.pghOktaService.isTokenValid();
    if (this.isAuthenticated) {
      this.router.navigate(['/']);
    } else {
      this.route.queryParams.subscribe(params => { if (params[contractnumberQueryParam]) this.contractNumberControl.setValue(atob(params[contractnumberQueryParam])); });
      this.heading = this.resourceService.getViewLabel('Signup_NewContract_Heading_Text');
      this.reCaptchaInvisbleKey = this.resourceService.getPortalSetting('Global_ReCaptcha2Invisible').Value;
    }
  }

  recaptchaExecute(reCaptcha) {
    // NOTE : Disable recaptcha to enable testing
    let isRecpatchEnabled = this.resourceService.isPortalSettingTrue('Global_Recaptcha_Feature');
    if (!isRecpatchEnabled || this.syntheticToken !== '') {
      this.onNextZipBtn(this.syntheticToken, reCaptcha);
    } else {
      reCaptcha.execute();
    }
  }

  onNextZipBtn(token, reCaptcha) {
    this.accountService.getSignup(this.contractNumberControl.value, this.zipControl.value, this.preInitAppSettings.getSiteSettings().groupId, token)
      .subscribe(
        (response: ISignupResult) => {
          this.maskEmailAddress = response?.emailAddress;
          this.userAlreadyExist = response?.userAlreadyExist;
          this.googleAnalyticsService.isPrimary = true;
          this.googleAnalyticsService.contractId = this.contractNumberControl.value;
          if (response?.userAlreadyExist) {
            this.userAlreadyExistInUserGroup = response.userAlreadyExistInUserGroup;
            if (this.userAlreadyExistInUserGroup) {
              this.currentScreenFlow = AccountSetupFlow.weKnowYou;
            } else {
              this.currentScreenFlow = AccountSetupFlow.emailConfirm;
            }
          } else {
            this.currentScreenFlow = AccountSetupFlow.emailConfirm;
          }
        },
        (error: HttpErrorResponse) => {
          reCaptcha.reset();
          let errorMsg = this.resourceService.getViewLabel('Signup_NewContract_ContractLookup_GenericError_Text');
          if (error.error && typeof error.error === 'string') {
            if (error.error.toLowerCase().includes('cancelled')) {
              errorMsg = this.resourceService.getViewLabel('Signup_NewContract_ContractLookup_ContractCancelled_Text');
            } else if (error.error.toLowerCase().includes('expired')) {
              errorMsg = this.resourceService.getViewLabel('Signup_NewContract_ContractLookup_ContractInactive_Text');
            }
          }
          this.contractZipError = errorMsg;
        }
      );
  }

  emailConfirmed(event) {
    if (this.userAlreadyExist && !this.userAlreadyExistInUserGroup) {
      this.postSignUp(this.contractNumberControl.value, '', '', '', true);
    } else {
      this.currentScreenFlow = AccountSetupFlow.passwordSetup;
    }
  }

  passwordChanged(event) {
    this.password = event.password;
    this.mobileNumber = event.mobileNumber;

    //need a default avatarUrl
    this.isSignupCompleting = true;
    this.avatarUrl = "";
    this.postSignUp(this.contractNumberControl.value, this.password, this.mobileNumber, this.avatarUrl);
  }

  changeHeading() {
    this.heading = this.resourceService.getViewLabel('Signup_Success_Heading_Text');
    this.cd.detectChanges();
  }

  setWeKnowYouHeading() {
    this.heading = this.resourceService.getViewLabel('Signup_NewContract_ContractFound_Heading_Text');
    this.cd.detectChanges();
  }

  postSignUp(contractId: string, password: string, mobileNumber: string, avtarUrl: string, isGroupSetup = false) {
    this.accountService.postSignup(contractId, password, mobileNumber, avtarUrl, isGroupSetup)
      .subscribe(() => {
        // Move to success flow
        this.googleAnalyticsService.contractId = contractId;
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.CreateLogin);
        this.currentScreenFlow = AccountSetupFlow.success;        
      }, (err) => {
        const emitError = {
          message: err?.error?.Message,
          type: err?.error?.ExceptionType,
          code: err?.error?.ErrorCode,
        }
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Failed, OnboardingSteps.CreateLogin, emitError);
        if (err.error && err.error.Message) {
          const oktaErrorCode = 'E0000001';
          const loginErrorCode = '(400, E0000001): login:';
          const passwordErrorCode = '(400, E0000001): password:';
          if (err.error.Message.indexOf(loginErrorCode) > -1) {
            err.error.Message = err.error.Message.substring(err.error.Message.indexOf(loginErrorCode) + loginErrorCode.length).trim();
            this.router.navigate(['/login']).then(() => {
              window.location.reload();
            });
            // TODO: ERROR : Add the Logic in API to raise Error with right ErrorCode
            err.error.CustomTitle = this.globalModalWindowService.getErrorContent(oktaErrorCode.toLowerCase(), err.error);
            throw err;
          } else if (err.error.Message.indexOf('') > -1) {
            // TODO: ERROR : Add the Logic in API to raise Error with right ErrorCode
            err.error.Message = err.error.Message.substring(err.error.Message.indexOf(passwordErrorCode) + passwordErrorCode.length).trim();
          }
            // TODO: ERROR : Add the Logic in API to raise Error with right ErrorCode
            err.error.CustomTitle = err.error.Message;
        }
        this.isSignupCompleting = false;
        this.currentScreenFlow = AccountSetupFlow.passwordSetup;
        throw err;
      }, () => {
        this.isSignupCompleting = false;
      });
  }

  // login controls not loading to close button is handled here
  onClose() {
    this.router.navigate(['/login'], { queryParamsHandling: 'preserve' }).then(
      () => { window.location.reload(); });
  }
}
